import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { IKeyValue } from 'src/app/models/ikeyvalue.interface';
import { IPerson, PersonOnlineRegForm, IPersonQueryResult } from 'src/app/models/iperson.interface';
import { IPersonConferenceTableResource, PersonPaymentForm, PersonPlaceExistRegForm, PersonPlaceNewRegForm, PersonRegistrationResource, PersonVisitForm } from 'src/app/models/iregistration.interface';
import { HTTP_CONTENT_HEADERS } from 'src/app/shared/data/const.data';
import { environment } from 'src/environments/environment';
import { MessageToastService } from '../common/message-toast.service';
import { IError } from 'src/app/models/ierror.interface';

@Injectable({
  providedIn: 'root'
})
export class RegService {

  private url = 'reg';
  private api = environment.apiUrl;

  constructor(private http: HttpClient, private msg: MessageToastService) { }

  regOnline(alias: string, form: PersonOnlineRegForm) {
    return this.http.post<PersonOnlineRegForm>(`${this.api}${this.url}/${alias}/online`, form);
  }
  regTable(conferenceId: number): Observable<IPerson[]> {
    return this.http.get<IPerson[]>(`${this.api}${this.url}/conf/${conferenceId}/table`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  regNew(conferenceId: number, form: PersonPlaceNewRegForm): Observable<PersonRegistrationResource> {
    return this.http.post<PersonRegistrationResource>(`${this.api}${this.url}/conf/${conferenceId}/newreg`, form).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  regExist(conferenceId: number, personId: number, form: PersonPlaceExistRegForm): Observable<PersonRegistrationResource> {
    return this.http.post<PersonRegistrationResource>(`${this.api}${this.url}/conf/${conferenceId}/${personId}`, form).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  regGet(conferenceId: number, personId: number): Observable<PersonRegistrationResource> {
    return this.http.get<PersonRegistrationResource>(`${this.api}${this.url}/conf/${conferenceId}/${personId}`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  regGetDirect(personconferenceId: number): Observable<PersonRegistrationResource> {
    return this.http.get<PersonRegistrationResource>(`${this.api}${this.url}/${personconferenceId}`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  regVisit(conferenceId: number, personId: number, form: PersonVisitForm): Observable<PersonRegistrationResource> {
    return this.http.post<PersonRegistrationResource>(`${this.api}${this.url}/conf/${conferenceId}/${personId}/visit`, form).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }

  regPayment(conferenceId: number, personId: number, form: PersonPaymentForm): Observable<PersonRegistrationResource> {
    return this.http.post<PersonRegistrationResource>(`${this.api}${this.url}/conf/${conferenceId}/${personId}/payment`, form).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }

  setMark(conferenceId: number, personId: number): Observable<PersonRegistrationResource> {
    return this.http.put<PersonRegistrationResource>(`${this.api}${this.url}/conf/${conferenceId}/${personId}/mark`, {}).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }

  delete(conferenceId: number, personId: number) {
    return this.http.delete(`${this.api}${this.url}/conf/${conferenceId}/${personId}`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }

  setCertificate(conferenceId: number, personId: number): Observable<PersonRegistrationResource> {
    return this.http.put<PersonRegistrationResource>(`${this.api}${this.url}/conf/${conferenceId}/${personId}/certificate/new`, {}).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }
  deleteCertificate(conferenceId: number, personId: number): Observable<PersonRegistrationResource> {
    return this.http.put<PersonRegistrationResource>(`${this.api}${this.url}/conf/${conferenceId}/${personId}/certificate/delete`, {}).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }

  pdfBadge(personConferenceId: number) {
    const url = `${this.api}${this.url}/${personConferenceId}/badge`;

    let hdrs = new HttpHeaders();
    hdrs = hdrs.set('Accept', HTTP_CONTENT_HEADERS.Pdf)

    return this.http.get(url, { observe: 'response', headers: hdrs, responseType: 'blob' }).pipe(
      catchError(err => { this.msg.errorEx(err); return EMPTY; }),
      map((res) => (new Blob([res.body], { type: HTTP_CONTENT_HEADERS.Pdf }))),
      shareReplay()
    );
  }

  pdfInvitation(personConferenceId: number) {
    const url = `${this.api}${this.url}/${personConferenceId}/invitation`;

    let hdrs = new HttpHeaders();
    hdrs = hdrs.set('Accept', HTTP_CONTENT_HEADERS.Pdf)

    return this.http.put(url, {}, { observe: 'response', headers: hdrs, responseType: 'blob' }).pipe(
      catchError(err => { this.msg.errorEx(err); return EMPTY; }),
      map((res) => (new Blob([res.body], { type: HTTP_CONTENT_HEADERS.Pdf }))),
      shareReplay()
    );
  }


  sendInvitation(personConferenceId: number) {
    let hdrs = new HttpHeaders();
    hdrs = hdrs.set('Accept', HTTP_CONTENT_HEADERS.Pdf)

    return this.http.put<IError>(`${this.api}${this.url}/${personConferenceId}/invitation/send`, {}).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }

  sendNmo(personConferenceId: number) {
    let hdrs = new HttpHeaders();
    hdrs = hdrs.set('Accept', HTTP_CONTENT_HEADERS.Pdf)

    return this.http.put<IError>(`${this.api}${this.url}/${personConferenceId}/nmo/send`, {}).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }


  pdfNmo(personConferenceId: number) {
    const url = `${this.api}${this.url}/${personConferenceId}/nmo`;

    let hdrs = new HttpHeaders();
    hdrs = hdrs.set('Accept', HTTP_CONTENT_HEADERS.Pdf)

    return this.http.get(url, { observe: 'response', headers: hdrs, responseType: 'blob' }).pipe(
      catchError(err => { this.msg.errorEx(err); return EMPTY; }),
      map((res) => (new Blob([res.body], { type: HTTP_CONTENT_HEADERS.Pdf }))),
      shareReplay()
    );
  }

  transferPersons(conferenceId: number, conferenceNewId: number, personConferenceIds: number[]) {
    return this.http.post(`${this.api}${this.url}/conf/${conferenceId}/transfer/${conferenceNewId}`, personConferenceIds).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
    );
  }



}
